import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { List, Map } from 'immutable';
import { of, Subject } from 'rxjs';
import { DialogService } from '../../../dialog/dialog.service';
import { DocumentScheduleIntervalEnum } from '../../../document-data/document-data.data';
import { Schedule } from '../../../document-data/sub-models/schedule.model';
import { BannerCanvasTypesEnum } from '../../../features-data/features-data.data';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import { ISelectOption } from '../../../form-master/components/select-field/select-field.interface';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import { FriendlyDatePipe } from '../../../shared/pipes/friendly-date/friendly-date.pipe';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { DocumentScheduleBuilder } from './document-schedule.builder';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-document-schedule',
  templateUrl: './document-schedule.component.html',
  styleUrls: ['./document-schedule.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [DocumentScheduleBuilder],
})
export class DocumentScheduleComponent implements OnInit, OnChanges, OnDestroy {
  @Input() documentDate: string;
  @Input() documentSchedule: Schedule;
  @Input() documentTrackingObject: ITrackingObject;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() featureToggles: Toggles;

  @Output() updateRecurringSchedule: EventEmitter<
    Map<string, any>
  > = new EventEmitter<Map<string, any>>();
  @Output() onGrowthCta: EventEmitter<void> = new EventEmitter<void>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();

  nextRunDateInfo: string;
  selectOptions: List<ISelectOption>;
  scheduleControl: FormControl;
  featureHighlightIcon: IIcon;
  recurringDropdownTapped: ITrackingAction;
  recurringDropdownSelected: ITrackingAction;
  recurringAdditionalProperties: any;

  constructor(
    private _documentScheduleBuilder: DocumentScheduleBuilder,
    private _translateService: TranslateService,
    private _dialogService: DialogService,
    private _featuresDataStore: FeaturesDataStore,
    private _friendlyDatePipe: FriendlyDatePipe,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingDispatcher: TrackingDispatcher
  ) {}

  ngOnChanges(changes) {
    const documentScheduleChanges: SimpleChange = changes.documentSchedule;

    if (!this.scheduleControl) {
      this.scheduleControl = new FormControl();
    }

    if (documentScheduleChanges) {
      const schedule = this.documentSchedule;
      const recurringInterval: string = schedule ? schedule.interval : null;
      const nextRunDate = schedule ? schedule.nextRunDate : null;
      let scheduleFrequencyDays: number;

      this.scheduleControl.setValue(recurringInterval, { emitEvent: false });

      if (nextRunDate) {
        const translateValues = {
          date: this._friendlyDatePipe.transform(nextRunDate),
        };

        this.nextRunDateInfo = this._translateService.instant(
          'DOCUMENT.INVOICE.RECURRING.NEXT_SCHEDULE',
          translateValues
        );
      } else {
        this.nextRunDateInfo = null;
      }

      switch (recurringInterval) {
        case DocumentScheduleIntervalEnum.weekly:
          scheduleFrequencyDays = 7;
          break;
        case DocumentScheduleIntervalEnum.fortnightly:
          scheduleFrequencyDays = 14;
          break;
        case DocumentScheduleIntervalEnum.monthly:
          scheduleFrequencyDays = 30;
          break;
        default:
          scheduleFrequencyDays = -1;
          break;
      }

      this.recurringAdditionalProperties = {
        schedule_frequency_days: scheduleFrequencyDays,
      };

      if (this.documentTrackingObject && this.recurringDropdownSelected) {
        this._trackingDispatcher.trackEvent(
          this.documentTrackingObject,
          this.recurringDropdownSelected.setValue(recurringInterval),
          this.recurringAdditionalProperties
        );
      }
    }

    if (changes.featureToggles && this.featureToggles) {
      this.selectOptions = this._documentScheduleBuilder.buildDocumentScheduleOptions(
        this.featureToggles.isAllAccess(Toggles.KEYS.SCHEDULE)
      );
    }
  }

  ngOnInit() {
    this.recurringDropdownTapped = this._trackingActionBuilder.buildDropdownTapped(
      'invoice_options_recurring',
      this.trackingScreenName
    );
    this.recurringDropdownSelected = this._trackingActionBuilder.buildDropdownSelected(
      'invoice_options_recurring',
      this.trackingScreenName
    );

    this._featuresDataStore
      .hasFeatureHighlightCanvas(Toggles.KEYS.SCHEDULE)
      .pipe(
        takeUntil(this._destroyed),
        filter((x) => !!x)
      )
      .subscribe(() => {
        this.featureHighlightIcon = {
          name: 'premium-feature',
          size: IconSizesEnum.medium,
          customClass: 'premium-icon',
        };
      });

    this.scheduleControl.valueChanges
      .pipe(takeUntil(this._destroyed))
      .subscribe((value) => {
        const payload = this._documentScheduleBuilder.buildSchedulePayload(
          value,
          this.documentDate
        );

        this.updateRecurringSchedule.emit(payload);
      });
  }

  showFeatureHighlightDialog() {
    this._dialogService.openGrowthDialog(
      BannerCanvasTypesEnum.feature_highlight,
      Toggles.KEYS.SCHEDULE,
      this.onGrowthCta.emit.bind(this.onGrowthCta)
    );
  }

  showUpgradeDialog(selectOption: ISelectOption): Promise<ISelectOption> {
    const featureName = Toggles.KEYS.SCHEDULE;
    const allAccess = this.featureToggles.isAllAccess(featureName);

    if (!allAccess) {
      this._dialogService.openGrowthDialog(
        BannerCanvasTypesEnum.feature_lock,
        featureName,
        this.onGrowthCta.emit.bind(this.onGrowthCta)
      );
    }

    return of(allAccess ? selectOption : null).toPromise();
  }

  showUpgradeDialogCallback = this.showUpgradeDialog.bind(this);

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
